import SaveIcon from '@mui/icons-material/Save';
import { CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ImageBox } from '../../components/BoxedImage';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import {
  configItemsToRecord,
  usePlanogramComplianceConfig,
} from '../../hooks/usePlanogramComplianceConfig';
import { useVariantSelection } from '../../hooks/useVariantSelection';
import { ApiService, composeProductRecognitionFieldsWithShapes } from '../../services/api';
import { GtApiService, GtSampleResponse } from '../../services/gtApi';
import { PlanogramComplianceResponse, ProductRecognitionResponse } from '../../types';
import { Audit, DetectionPogPairing } from '../tasks/components/Audit';
import { VariantSelector } from './components/VariantSelector';

interface GroundTruthAuditProps {
  gtSampleId: string | null;
}

export const GroundTruthAudit: React.FC<GroundTruthAuditProps> = ({ gtSampleId }) => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();

  const [productRecognitionData, setProductRecognitionData] =
    useState<ProductRecognitionResponse | null>(null);
  const [planogramComplianceData, setPlanogramComplianceData] =
    useState<PlanogramComplianceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingGt, setIsSavingGt] = useState(false);
  const [imageId, setImageId] = useState<string | null>(null);
  const { config: planogramComplianceConfig } = usePlanogramComplianceConfig(
    imageId ? Number(imageId) : null
  );

  const [storeName, setStoreName] = useState<string | null>(null);
  const [currentPairings, setCurrentPairings] = useState<Map<string, DetectionPogPairing>>(
    new Map()
  );

  const {
    loadingUpcs,
    fetchVariantsForUpc,
    selectVariant,
    clearVariant,
    getSelectedVariant,
    getVariantsForUpc,
    allVariantsSelected,
    selectedVariants,
  } = useVariantSelection();

  // Track previous pairing keys to detect deletions
  const prevPairingKeysRef = useRef<Set<string>>(new Set());

  const fetchGtSample = useCallback(async () => {
    if (!gtSampleId || !token) return;
    setIsLoading(true);
    try {
      const gtSample: GtSampleResponse = await GtApiService.getGtSample(token, gtSampleId);
      setImageId(gtSample.imageId != null ? String(gtSample.imageId) : null);
      const prData = gtSample.prInference as ProductRecognitionResponse | undefined;
      if (prData) {
        const fieldsWithShapes = composeProductRecognitionFieldsWithShapes(prData);
        setProductRecognitionData({ ...prData, ...fieldsWithShapes });
      }
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to fetch GT sample');
    } finally {
      setIsLoading(false);
    }
  }, [gtSampleId, token, showErrorToast]);

  useEffect(() => {
    fetchGtSample();
  }, [fetchGtSample]);

  useEffect(() => {
    const fetchPlanogramCompliance = async () => {
      if (
        !token ||
        !productRecognitionData ||
        planogramComplianceData ||
        !planogramComplianceConfig
      ) {
        return;
      }
      try {
        const payload = {
          ...productRecognitionData,
          config: configItemsToRecord(planogramComplianceConfig),
        };
        const data = await ApiService.createPlanogramComplianceFromPayload(token, payload, true);
        setPlanogramComplianceData(data);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Failed to fetch planogram compliance');
      }
    };
    fetchPlanogramCompliance();
  }, [
    token,
    productRecognitionData,
    planogramComplianceData,
    planogramComplianceConfig,
    showErrorToast,
  ]);

  // Fetch variants when pairings change
  useEffect(() => {
    if (!token || !storeName) return;

    // Fetch variants for any new UPCs (skip null-UPC pairings)
    for (const [, pairing] of currentPairings) {
      if (pairing.upc) fetchVariantsForUpc(token, storeName, pairing.upc);
    }

    // Clean up selections for deleted pairings
    const currentKeys = new Set(currentPairings.keys());
    for (const prevKey of prevPairingKeysRef.current) {
      if (!currentKeys.has(prevKey)) {
        clearVariant(prevKey);
      }
    }
    prevPairingKeysRef.current = currentKeys;
  }, [currentPairings, token, storeName, fetchVariantsForUpc, clearVariant]);

  const handleDetectionPogPairingsChange = useCallback(
    (pairings: Map<string, DetectionPogPairing>) => {
      setCurrentPairings(pairings);
    },
    []
  );

  const handleStoreNameChange = useCallback((name: string) => {
    setStoreName(name);
  }, []);

  const handleVariantSelect = useCallback(
    (detectionKey: string, variant: import('../../types').ProductVariantResponse) => {
      selectVariant(detectionKey, variant);
    },
    [selectVariant]
  );

  const pairingAddon = useCallback(
    (pairing: DetectionPogPairing & { detectionKey: string }) => {
      if (!pairing.upc) return null;

      const variants = getVariantsForUpc(pairing.upc) || [];
      const selected = getSelectedVariant(pairing.detectionKey);
      const isLoadingVariants = loadingUpcs.has(pairing.upc);

      return (
        <VariantSelector
          detectionKey={pairing.detectionKey}
          variants={variants}
          selectedVariant={selected}
          isLoading={isLoadingVariants}
          onSelectVariant={handleVariantSelect}
        />
      );
    },
    [getVariantsForUpc, getSelectedVariant, loadingUpcs, handleVariantSelect]
  );

  const applyVariantsToPrData = useCallback(
    (prData: ProductRecognitionResponse): ProductRecognitionResponse => {
      if (selectedVariants.size === 0) return prData;

      const modified: ProductRecognitionResponse = JSON.parse(JSON.stringify(prData));

      // Build a map from shape key to selected variant
      const shapeToVariant = new Map<string, import('../../types').ProductVariantResponse>();
      for (const [detectionKey, variant] of selectedVariants) {
        const pairing = currentPairings.get(detectionKey);
        if (pairing?.detection.shape) {
          shapeToVariant.set(pairing.detection.shape.join(','), variant);
        }
      }

      Object.keys(modified).forEach((layerKey) => {
        const layer = modified[layerKey as keyof ProductRecognitionResponse];
        if (Array.isArray(layer)) {
          layer.forEach((detection: any) => {
            if (detection.shape) {
              const shapeKey = Array.isArray(detection.shape) ? detection.shape.join(',') : '';
              const variant = shapeToVariant.get(shapeKey);
              if (variant && detection.vizSimCandidates?.length > 0) {
                detection.vizSimCandidates[0].normalizedBarcode = variant.upc;
                detection.vizSimCandidates[0].normalizedUpc = variant.upc;
                if (variant.name) {
                  detection.vizSimCandidates[0].productName = variant.name;
                }
                if (variant.frontImage) {
                  detection.vizSimCandidates[0].productImageUri = variant.frontImage;
                }
              }
            }
          });
        }
      });

      return modified;
    },
    [selectedVariants, currentPairings]
  );

  const renderTitleActions = useCallback(
    ({
      selectedDetections,
      setDetectionPogPairings,
      setSelectedDetections,
    }: {
      selectedDetections: ImageBox[];
      setDetectionPogPairings: React.Dispatch<
        React.SetStateAction<Map<string, DetectionPogPairing>>
      >;
      setSelectedDetections: React.Dispatch<React.SetStateAction<ImageBox[]>>;
    }) => {
      const handleSpecialPairing = (
        type: 'unidentifiable' | 'no_data',
        confidence: number,
        isConfident: boolean
      ) => {
        if (selectedDetections.length === 0) return;

        setDetectionPogPairings((prev) => {
          const newMap = new Map(prev);
          selectedDetections.forEach((detection) => {
            if (!detection.shape) return;
            const detectionKey = detection.shape.join(',');
            newMap.set(detectionKey, {
              detection,
              upc: null,
              name: type === 'unidentifiable' ? 'Unidentifiable' : 'No data',
              productId: null,
              source: type,
              confidence,
              isConfident,
              timestamp: new Date(),
            });
          });
          return newMap;
        });
        setSelectedDetections([]);
      };

      return (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSpecialPairing('unidentifiable', 0.5, false)}
            disabled={selectedDetections.length === 0}
            className="px-3 py-1 rounded text-sm font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Unidentifiable
          </button>
          <button
            onClick={() => handleSpecialPairing('no_data', -1, true)}
            disabled={selectedDetections.length === 0}
            className="px-3 py-1 rounded text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            No data
          </button>
        </div>
      );
    },
    []
  );

  const applyPairingsToPrData = useCallback(
    (prData: ProductRecognitionResponse): ProductRecognitionResponse => {
      if (currentPairings.size === 0) return prData;

      const modified: ProductRecognitionResponse = JSON.parse(JSON.stringify(prData));

      Object.keys(modified).forEach((layerKey) => {
        const layer = modified[layerKey as keyof ProductRecognitionResponse];
        if (Array.isArray(layer)) {
          layer.forEach((detection: any) => {
            if (detection.shape) {
              const shapeKey = Array.isArray(detection.shape) ? detection.shape.join(',') : '';
              const pairing = currentPairings.get(shapeKey);

              if (pairing) {
                const pairingConfidence = pairing.confidence ?? 1;
                const pairingIsConfident = pairing.isConfident ?? true;

                detection.confidence = pairingConfidence;

                if (!detection.vizSimCandidates) {
                  detection.vizSimCandidates = [];
                }

                if (pairing.upc === null) {
                  if (detection.vizSimCandidates.length > 0) {
                    detection.vizSimCandidates[0].isConfident = pairingIsConfident;
                    detection.vizSimCandidates[0].normalizedBarcode = null;
                    detection.vizSimCandidates[0].similarity = pairingIsConfident ? 1 : 0;
                  } else {
                    detection.vizSimCandidates.unshift({
                      normalizedUpc: null,
                      normalizedBarcode: null,
                      similarity: pairingIsConfident ? 1 : 0,
                      isConfident: pairingIsConfident,
                      productName: pairing.name || '',
                      itemNumber: '',
                      productImageUri: '',
                      productId: undefined,
                    });
                  }
                } else {
                  const existingCandidateIndex = detection.vizSimCandidates.findIndex(
                    (candidate: any) => candidate.normalizedBarcode === pairing.upc
                  );

                  if (existingCandidateIndex !== -1) {
                    detection.vizSimCandidates[existingCandidateIndex].isConfident =
                      pairingIsConfident;
                    detection.vizSimCandidates[existingCandidateIndex].similarity = 1;
                    detection.vizSimCandidates.sort(
                      (a: any, b: any) => (b.similarity || 0) - (a.similarity || 0)
                    );
                  } else {
                    detection.vizSimCandidates.unshift({
                      normalizedUpc: pairing.upc,
                      normalizedBarcode: pairing.upc,
                      similarity: 1,
                      isConfident: pairingIsConfident,
                      productName: pairing.name || '',
                      itemNumber: '',
                      productImageUri: pairing.imageUri || pairing.pogBox?.imageUri || '',
                      productId: undefined,
                    });
                  }
                }
              }
            }
          });
        }
      });

      return modified;
    },
    [currentPairings]
  );

  const handleSaveGt = useCallback(async () => {
    if (!gtSampleId || !token || !productRecognitionData) return;
    setIsSavingGt(true);
    try {
      const withPairings = applyPairingsToPrData(productRecognitionData);
      const finalPrData = applyVariantsToPrData(withPairings);
      const payload = { pr_inference: finalPrData };
      await GtApiService.updateGtSamplePr(token, gtSampleId, payload);
      showSuccessToast('GT inference saved successfully');
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to save GT sample');
    } finally {
      setIsSavingGt(false);
    }
  }, [
    gtSampleId,
    token,
    productRecognitionData,
    applyPairingsToPrData,
    applyVariantsToPrData,
    showErrorToast,
    showSuccessToast,
  ]);

  const hasPairingsNeedingVariants = currentPairings.size > 0;
  const variantsReady = allVariantsSelected(currentPairings);
  const saveDisabled =
    isSavingGt || !productRecognitionData || (hasPairingsNeedingVariants && !variantsReady);

  const saveButton = (
    <button
      onClick={handleSaveGt}
      disabled={saveDisabled}
      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
    >
      {isSavingGt ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
      {isSavingGt ? 'Saving...' : 'Save GT'}
    </button>
  );

  return (
    <Audit
      productRecognitionData={productRecognitionData}
      planogramComplianceData={planogramComplianceData}
      isLoading={isLoading}
      imageId={imageId}
      onProductRecognitionDataChange={setProductRecognitionData}
      onPlanogramComplianceDataChange={setPlanogramComplianceData}
      actionButtons={saveButton}
      defaultPlanogramComplianceConfig={planogramComplianceConfig}
      pairingAddon={pairingAddon}
      onDetectionPogPairingsChange={handleDetectionPogPairingsChange}
      onStoreNameChange={handleStoreNameChange}
      renderTitleActions={renderTitleActions}
    />
  );
};

export default GroundTruthAudit;
